export const Environment = {
  Local: "local",
  Preprod: "preprod",
  Demo: "demo",
  Production: "production",
} as const;
export type Environment = (typeof Environment)[keyof typeof Environment];

function isVitest(): boolean {
  return typeof process !== "undefined" && process.env["VITEST"] === "true";
}

export function getEnvironment(): Environment {
  // Check if running in Vitest first
  if (isVitest()) {
    return Environment.Local;
  }

  // Vite dev override: VITE_LOCAL_OVERRIDE_ENVIRONMENT=preprod|demo|production
  // points API + cookie + Auth0 + Stripe at the remote env while running on
  // localhost. Used during component renovation work for visual verification
  // against real customer data. Auth0's Resource Owner Password Grant flow
  // (useSignIn.tsx) doesn't need redirect-URI registration, so the user signs
  // in with their real remote credentials and gets a token that validates
  // against the remote API.
  const override = import.meta.env["VITE_LOCAL_OVERRIDE_ENVIRONMENT"];
  if (
    override === Environment.Preprod ||
    override === Environment.Demo ||
    override === Environment.Production
  ) {
    return override as Environment;
  }

  // Try to get environment from meta tag first
  const envMeta = document.querySelector('meta[name="environment"]');
  const metaEnvironment = envMeta?.getAttribute("content");

  if (metaEnvironment === undefined) {
    throw new Error("Meta environment tag not found");
  }

  if (metaEnvironment === "%ENVIRONMENT%") {
    return Environment.Local;
  }

  // Check if the value exists in Environment enum
  if (Object.values(Environment).includes(metaEnvironment as Environment)) {
    return metaEnvironment as Environment;
  }

  throw new Error(
    `Should not be here - problem with meta environment tag: ${metaEnvironment}`,
  );
}

export function isLocalOverride(): boolean {
  return !!import.meta.env["VITE_LOCAL_OVERRIDE_ENVIRONMENT"];
}

export function isProduction(): boolean {
  return getEnvironment() === Environment.Production;
}

export function isDemo(): boolean {
  return getEnvironment() === Environment.Demo;
}

export function isPreprod(): boolean {
  return getEnvironment() === Environment.Preprod;
}

export function isLocal(): boolean {
  return getEnvironment() === Environment.Local;
}

export function apiServerUrl(): string {
  const environment = getEnvironment();
  switch (environment) {
    case Environment.Local:
      return import.meta.env["VITE_FS_API_URL"] || "http://localhost:8001";
    case Environment.Preprod:
      return "https://api.fspreprod.com";
    case Environment.Demo:
      return "https://api.freightsimpledemo.com";
    case Environment.Production:
      return "https://api.freightsimple.com";
  }
}

export function cookieDomain(): string {
  const environment = getEnvironment();
  switch (environment) {
    case Environment.Production:
      return "freightsimple.com";
    case Environment.Demo:
      return "freightsimpledemo.com";
    case Environment.Preprod:
      return "fspreprod.com";
    case Environment.Local:
      return "localhost";
  }
}

interface Auth0Creds {
  domain: string;
  clientId: string;
  audience: string;
}

function getAuth0Domain(): string {
  const environment = getEnvironment();
  switch (environment) {
    case Environment.Local:
      return "freightsimple-local.auth0.com";
    case Environment.Preprod:
      return "freightsimple-preprod.auth0.com";
    case Environment.Demo:
      return "freightsimple-demo.us.auth0.com";
    case Environment.Production:
      return "freightsimple-production.auth0.com";
  }
}

function getAuth0ClientId(): string {
  const environment = getEnvironment();
  switch (environment) {
    case Environment.Local:
      return "CbTvxGPy0INxRqvGoTZNMhfmH7BD0yDX";
    case Environment.Preprod:
      return "7Wpmu72Ku0sampTsq66WT2gdiAAcrqwK";
    case Environment.Demo:
      return "1u4UXU3MqHs0Jxk4iGlvUlpcL650irsL";
    case Environment.Production:
      return "wtanv5Dz8sZU340M55sEsWkc4cIBVQgJ";
  }
}

function getAuth0Audience(): string {
  const environment = getEnvironment();
  switch (environment) {
    case Environment.Local:
      // Audience is a fixed Auth0-registered identifier, NOT the API URL.
      // Auth0 allows only one audience per API; the dashboard's slot port
      // varies per checkout (8780, 8880, ...). The audience stays
      // constant regardless of port. Backend's expected audience is also
      // `http://localhost:8080`.
      return "http://localhost:8080";
    case Environment.Preprod:
      return "https://api.fspreprod.com";
    case Environment.Demo:
      return "https://api.freightsimpledemo.com";
    case Environment.Production:
      return "https://api.freightsimple.com";
  }
}

export function auth0Creds(): Auth0Creds {
  return {
    domain: getAuth0Domain(),
    clientId: getAuth0ClientId(),
    audience: getAuth0Audience(),
  };
}

export function rootUrl(): string {
  const environment = getEnvironment();
  switch (environment) {
    case Environment.Local:
      return (
        (import.meta.env["VITE_FS_DASHBOARD_URL"] || "http://localhost:3001") +
        "/"
      );
    case Environment.Preprod:
      return "https://dashboard.fspreprod.com/";
    case Environment.Demo:
      return "https://dashboard.freightsimpledemo.com/";
    case Environment.Production:
      return "https://dashboard.freightsimple.com/";
  }
}

export function getStripeKey(): string {
  if (isProduction()) {
    return "pk_live_23rWgGvkbZhsZ9dWnezporWD00KZhNv5FG";
  } else {
    return "pk_test_6GHYhSyObuzDjZHy9zKOactB00hc9juaCE";
  }
}
